import { useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

async function ensureUserDoc(emailRaw) {
  const email = String(emailRaw || "").toLowerCase().trim();
  if (!email) return null;

  const ref = doc(db, "users", email);
  const snap = await getDoc(ref);

  // Only create if missing; never overwrite admin flags.
  if (!snap.exists()) {
    await setDoc(ref, { email, admin: false });
  }

  return ref;
}

export function useAuth() {
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed out
  const [admin, setAdmin] = useState(false);
  const [adminLoading, setAdminLoading] = useState(true);

  useEffect(() => {
    let unsubUserDoc = null;

    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUser(u || null);

      // Clean up prior user doc listener
      if (unsubUserDoc) {
        unsubUserDoc();
        unsubUserDoc = null;
      }

      if (!u?.email) {
        setAdmin(false);
        setAdminLoading(false);
        return;
      }

      setAdminLoading(true);

      (async () => {
        try {
          const ref = await ensureUserDoc(u.email);
          if (!ref) {
            setAdmin(false);
            setAdminLoading(false);
            return;
          }

          unsubUserDoc = onSnapshot(
            ref,
            (snap) => {
              const data = snap.data();
              setAdmin(!!data?.admin);
              setAdminLoading(false);
            },
            () => {
              setAdmin(false);
              setAdminLoading(false);
            }
          );
        } catch {
          setAdmin(false);
          setAdminLoading(false);
        }
      })();
    });

    return () => {
      if (unsubUserDoc) unsubUserDoc();
      unsubAuth();
    };
  }, []);

  return {
    user,
    loading: user === undefined,
    admin,
    adminLoading,
    email: user?.email || null,
  };
}
